/**
 * File: useAcademyQueries.ts
 * Path: apps/admin/src/shared/hooks/useAcademyQueries.ts
 * Description: Read-only data fetching for Academy posts using React Query and standardized collection keys.
 */

import { useQuery } from "@tanstack/react-query";
import {
  ADMIN_PAGE_SIZE,
  FirestoreService,
  FIRESTORE_COLLECTIONS,
} from "@idk/config";
import { AuthoritativeBlogPost } from "@idk/types";
import { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import { useAuthorDirectory } from "./useAuthorDirectoryQueries";

export const useAcademyQueries = () => {
  const collectionKey = FIRESTORE_COLLECTIONS.academy;

  // Members are hooks (use* names) so the rules-of-hooks lint applies to them.
  return {
    useList: () =>
      useQuery({
        queryKey: [collectionKey, "list"],
        queryFn: () =>
          FirestoreService.getAll<AuthoritativeBlogPost>(
            collectionKey,
            ADMIN_PAGE_SIZE,
          ),
      }),

    useById: (id?: string) =>
      useQuery({
        queryKey: [collectionKey, "detail", id],
        queryFn: () =>
          FirestoreService.getById<AuthoritativeBlogPost>(collectionKey, id!),
        enabled: !!id,
      }),

    useBySlug: (slug?: string) =>
      useQuery({
        queryKey: [collectionKey, "slug", slug],
        queryFn: () =>
          FirestoreService.findOne<AuthoritativeBlogPost>(
            collectionKey,
            "slug",
            slug,
          ),
        enabled: !!slug,
      }),

    usePage: (
      pageSize: number = 10,
      lastDoc?: QueryDocumentSnapshot<DocumentData>,
    ) =>
      useQuery({
        queryKey: [collectionKey, "page", pageSize, lastDoc?.id ?? null],
        queryFn: () =>
          FirestoreService.getPaginated<AuthoritativeBlogPost>(
            collectionKey,
            pageSize,
            lastDoc,
          ),
        placeholderData: (previousData) => previousData,
      }),

    // Shared with the portfolio editor; falls back to the signed-in user when
    // the tightened /users rule refuses the directory read (P2.6).
    useUsers: useAuthorDirectory,
  };
};
